import { type Theme } from '@mui/material';
import { backdropClasses } from '@mui/material/Backdrop';
import { common } from '@mui/material/colors';
import { filledInputClasses } from '@mui/material/FilledInput';
import { outlinedInputClasses } from '@mui/material/OutlinedInput';
import { paperClasses } from '@mui/material/Paper';
import type { Components } from '@mui/material/styles/components';
import type { PaletteColor, PaletteOptions } from '@mui/material/styles/createPalette';
import { tableCellClasses } from '@mui/material/TableCell';
import { alpha } from '@mui/system/colorManipulator';

import { neutral } from '../colors';

interface Config {
  palette: PaletteOptions;
}

export const createComponents = ({ palette }: Config): Components => {
  return {
    MuiAvatar: {
      styleOverrides: {
        root: {
          backgroundColor: palette.neutral![200],
          color: common.black,
        },
      },
    },
    MuiBackdrop: {
      styleOverrides: {
        root: {
          [`&:not(.${backdropClasses.invisible})`]: {
            backgroundColor: alpha(palette.neutral![900], 0.75),
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          [`&.${paperClasses.elevation1}`]: {
            boxShadow: '0px 5px 22px rgba(0, 0, 0, 0.04), 0px 0px 0px 0.5px rgba(0, 0, 0, 0.03)',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        icon: {
          color: palette.action!.active,
        },
        root: {
          borderColor: palette.neutral![200],
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        '#nprogress .bar': {
          backgroundColor: (palette.primary as PaletteColor).main,
        },
        '.slick-dots li button': {
          '&:before': {
            fontSize: 10,
            color: (palette.primary as PaletteColor).main,
          },
        },
        '.slick-dots li.slick-active button': {
          '&:before': {
            color: (palette.primary as PaletteColor).main,
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          height: 50,
          marginBottom: 6,
          '& .MuiInputBase-root': {
            height: '100%',
          },
          '& .MuiFormHelperText-root': {
            position: 'absolute',
            bottom: -20,
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        input: {
          letterSpacing: '0.15px',
          color: palette.text?.primary,
          '&::placeholder': {
            color: palette.text?.secondary,
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          letterSpacing: '0.15px',
          ...(ownerState.variant === 'filled' && {
            '&.Mui-focused': {
              color: palette.text?.secondary,
            },
          }),
          // Vertically center in non-focused/non-shrunk state
          '&:not(.Mui-focused):not(.MuiInputLabel-shrink)': {
            transform: 'translate(14px, 15px)', // Vertical centering
          },
        }),
      },
    },
    MuiFilledInput: {
      styleOverrides: {
        root: {
          borderColor: palette.neutral![200],
          '&:hover': {
            borderColor: palette.text?.secondary,
            backgroundColor: 'transparent',
          },
          [`&.${filledInputClasses.disabled}`]: {
            backgroundColor: 'transparent',
          },
          [`&.${filledInputClasses.focused}`]: {
            backgroundColor: 'transparent',
            borderColor: (palette.primary as PaletteColor).main,
          },
          [`&.${filledInputClasses.error}`]: {
            borderColor: (palette.error as PaletteColor).main,
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '&:hover': {
            [`& .${outlinedInputClasses.notchedOutline}`]: {
              borderColor: palette.text?.secondary,
            },
          },
          [`&.${outlinedInputClasses.focused}`]: {
            backgroundColor: 'transparent',
            [`& .${outlinedInputClasses.notchedOutline}`]: {
              borderColor: (palette.primary as PaletteColor).main,
            },
          },
          [`&.${outlinedInputClasses.error}`]: {
            [`& .${outlinedInputClasses.notchedOutline}`]: {
              borderColor: (palette.error as PaletteColor).main,
              borderWidth: '3px',
            },
          },
        },
        notchedOutline: {
          borderColor: palette.neutral![200],
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        popupIndicator: {
          color: (palette.secondary as PaletteColor)?.main,
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        switchBase: {
          color: palette.neutral![500],
        },
        track: {
          backgroundColor: palette.neutral![400],
          opacity: 1,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottomColor: palette.divider,
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          [`& .${tableCellClasses.root}`]: {
            backgroundColor: neutral[50],
            color: palette.text?.primary,
            textTransform: 'capitalize',
          },
        },
      },
    },
    // @ts-expect-error this is fine
    MuiTimelineConnector: {
      styleOverrides: {
        root: {
          backgroundColor: palette.divider,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backdropFilter: 'blur(6px)',
          background: alpha(palette.neutral![900], 0.8),
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          height: 3,
          borderRadius: '6px',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          fontSize: '16px',
          paddingBottom: '6px',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        sizeMedium: {
          padding: '6px 16px',
          fontSize: '14px',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        root: {
          padding: 0,
          '& .MuiDialogTitle-root': {
            padding: '24px',
            borderBottom: '1px solid',
            borderBottomColor: (palette.secondary as PaletteColor).light,
          },
          '& .MuiDialogContent-root': {
            padding: '24px',
          },
          '& .MuiDialogActions-root': {
            padding: '24px',
          },
        },
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: ({ theme }) => ({
          paddingLeft: 16,
          paddingRight: 16,
          paddingTop: 16,
          paddingBottom: 16,
          [(theme as Theme).breakpoints.up('sm')]: {
            padding: 32,
          },
        }),
      },
    },
  };
};
